import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { FileText, Download, AlertTriangle } from 'lucide-react';

export default function SavedReports() {
  const { token, API_URL } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/my-scans`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data =>
        setReports(
          Array.isArray(data) ? data.filter(s => s.riskLevel !== 'Low') : []
        )
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, API_URL]);

  const exportJSON = report => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), {
      href:     url,
      download: `report-${report.wallet?.slice(0, 10)}.json`,
    });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">Saved Reports</h1>
        <p className="text-slate-500 text-sm mt-0.5">
          Medium and high-risk wallets flagged during scans
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-14">
          <div className="w-6 h-6 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <div className="glass-card p-14 text-center">
          <FileText size={30} className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            No risk reports yet. Scan wallets to generate reports.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((r, i) => {
            const isHigh = r.riskLevel === 'High';
            return (
              <div
                key={i}
                className={`glass-card p-5 border ${
                  isHigh ? 'border-red-500/20' : 'border-yellow-500/15'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Badge + date */}
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle
                        size={13}
                        className={isHigh ? 'text-red-400' : 'text-yellow-400'}
                      />
                      <span
                        className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                          isHigh
                            ? 'bg-red-500/15 text-red-400'
                            : 'bg-yellow-500/15 text-yellow-400'
                        }`}
                      >
                        {r.riskLevel} Risk
                      </span>
                      <span className="text-[11px] text-slate-600 tabular-nums">
                        {new Date(r.date).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Wallet */}
                    <p className="font-mono text-sm text-slate-300 break-all mb-3">
                      {r.wallet}
                    </p>

                    {/* Stats */}
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-600">
                      <span>
                        Risk Score:{' '}
                        <span className="text-slate-300 font-semibold tabular-nums">{r.riskScore}</span>
                      </span>
                      <span>
                        Reputation:{' '}
                        <span className="text-slate-300 font-semibold tabular-nums">
                          {r.reputationScore}/100
                        </span>
                      </span>
                      <span>
                        Txns:{' '}
                        <span className="text-slate-300 font-semibold tabular-nums">
                          {r.txCount?.toLocaleString()}
                        </span>
                      </span>
                    </div>

                    {/* Warning tags */}
                    {r.warnings?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {r.warnings.map((w, j) => (
                          <span
                            key={j}
                            className="text-[11px] bg-red-500/10 text-red-400/80 border border-red-500/15 px-2 py-0.5 rounded-full"
                          >
                            {w}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Export button */}
                  <button
                    onClick={() => exportJSON(r)}
                    className="flex-shrink-0 flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#00E5FF] bg-white/[0.04] hover:bg-[#00E5FF]/8 border border-white/[0.07] hover:border-[#00E5FF]/25 px-3 py-1.5 rounded-lg transition-all"
                  >
                    <Download size={12} />
                    Export
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
